import { Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Box, Spinner, Text, VStack } from "@chakra-ui/react";
import { asHTML } from "@prismicio/client";
import { useGetFaqsByTags } from "hooks/query";
import { richTextSx } from "./richTextStyles";

const HEADING_INK = "#0E3137";
const GREEN = "#5A8A1A";

// Bumps the shared rich-text paragraph/list size up a step for FAQ answers
// (matches BodycontentSlice's same override pattern in this Help section).
const answerSx = { ...richTextSx, p: { ...richTextSx.p, fontSize: "body.l" }, li: { ...richTextSx.li, fontSize: "body.l" } };

// Same lozenge shape used elsewhere in the Help section (see LinkSlice.jsx) — dark
// green fill, light text.
function Lozenge({ text }) {
  return (
    <Text display="inline-block" px={3} py={1.5} fontSize="12px" fontWeight="600" bg={GREEN} color="white" borderRadius="full">
      {text}
    </Text>
  );
}

/**
 * The "faq" slice doesn't link to a fixed document — it defines a live tag filter
 * (repeatable groups of free-text tags authored in Prismic), and this component
 * queries ALL `faq` documents matching that filter at render time:
 *   - `faq_tags`                        -> AND: doc must have every one of these tags
 *   - `must_have_at_least_one_of_these`  -> OR: doc must have at least one of these
 *   - `must_not_have_any_of_these`       -> NOT: doc must have none of these
 * Any bucket left empty in Prismic is skipped. See api/prismic.js getFaqsByTags().
 */
function tagsFromGroup(group) {
  return (group || []).map((row) => row.tag).filter(Boolean);
}

export default function FaqSlice({ slice }) {
  const { heading, sub_heading } = slice.primary;
  const andTags = tagsFromGroup(slice.primary?.faq_tags);
  const orTags = tagsFromGroup(slice.primary?.must_have_at_least_one_of_these);
  const notTags = tagsFromGroup(slice.primary?.must_not_have_any_of_these);

  const { data: faqs, isLoading } = useGetFaqsByTags({ andTags, orTags, notTags });

  if (isLoading) return <Spinner size="sm" />;
  if (!faqs || faqs.length === 0) return null;

  return (
    <VStack align="stretch" gap={4}>
      <VStack align="center" gap={2}>
        <Lozenge text="FAQs" />
        {heading && (
          <Text fontSize="24px" fontWeight="700" color={HEADING_INK} textAlign="center">
            {heading}
          </Text>
        )}
      </VStack>
      {sub_heading?.length > 0 && <Box sx={answerSx} dangerouslySetInnerHTML={{ __html: asHTML(sub_heading) }} />}
      <Accordion allowToggle defaultIndex={[0]}>
        {faqs.map((faq) => {
          const question = faq.data?.question;
          const answer = faq.data?.answer;
          if (!question) return null;
          return (
            <AccordionItem key={faq.id} border="none" borderBottom="1px solid" borderColor="gray.100">
              <AccordionButton px={0} py={4}>
                <Text flex="1" textAlign="left" fontWeight="700" fontSize="body.l" color={HEADING_INK}>
                  {question}
                </Text>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel px={0} pb={4} pt={0}>
                {answer?.length > 0 && <Box sx={answerSx} dangerouslySetInnerHTML={{ __html: asHTML(answer) }} />}
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
    </VStack>
  );
}
